/* ==========================================================================
   Social Feed (KAN-127) — behaviour for sections/social-feed.liquid
   - Opens/closes the "shop the look + social info" pop-up (native <dialog>).
   - Autoplays muted tile videos while in view (IntersectionObserver).
   - Optional per-post sound toggle for autoplay videos.
   Guarded against double-loading: the <script> tag renders once per section
   instance, but this initialises a single set of delegated listeners.
   ========================================================================== */
(function () {
  if (window.__sfeedLoaded) return;
  window.__sfeedLoaded = true;

  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var CAN_HOVER = window.matchMedia && window.matchMedia('(hover: hover)').matches;

  function safePlay(video) {
    if (!video) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }

  /* ---------- Pop-up ----------------------------------------------------- */
  function openModal(dialog) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (dialog.open) return;
    dialog.showModal();
    var video = dialog.querySelector('.sfeed__modal-video');
    if (video) {
      video.currentTime = 0;
      video.muted = false;
      safePlay(video);
    }
  }

  function closeModal(dialog) {
    if (!dialog || !dialog.open) return;
    var video = dialog.querySelector('.sfeed__modal-video');
    if (video) {
      video.pause();
    }
    dialog.close();
  }

  document.addEventListener('click', function (event) {
    // Open
    var opener = event.target.closest('[data-sfeed-open]');
    if (opener) {
      var id = opener.getAttribute('aria-controls');
      var dialog = id && document.getElementById(id);
      if (dialog) {
        event.preventDefault();
        openModal(dialog);
      }
      return;
    }

    // Explicit close button
    var closer = event.target.closest('[data-sfeed-close]');
    if (closer) {
      event.preventDefault();
      closeModal(closer.closest('[data-sfeed-modal]'));
      return;
    }

    // Backdrop click (the click lands on the <dialog> element itself)
    if (event.target.matches('[data-sfeed-modal]')) {
      closeModal(event.target);
    }
  });

  // Native Escape / form-close: pause video when the dialog closes.
  document.addEventListener(
    'close',
    function (event) {
      var dialog = event.target;
      if (dialog && dialog.matches && dialog.matches('[data-sfeed-modal]')) {
        var video = dialog.querySelector('.sfeed__modal-video');
        if (video) video.pause();
      }
    },
    true
  );

  /* ---------- Sound toggle for autoplay tile videos ---------------------- */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-sfeed-mute]');
    if (!btn) return;
    event.preventDefault();
    var item = btn.closest('[data-sfeed-item]');
    var video = item && item.querySelector('.sfeed__video');
    if (!video) return;
    var unmute = video.muted; // if currently muted, we are turning sound ON
    video.muted = !unmute;
    btn.setAttribute('aria-pressed', unmute ? 'true' : 'false');
  });

  /* ---------- Tile video playback (per feed) ----------------------------- */
  // Each feed wrapper carries data-autoplay="true|false".
  //   true  → autoplay muted while scrolling into view (IntersectionObserver)
  //   false → paused; play on hover (desktop). Mobile has no hover, so the
  //           poster shows and the video only plays inside the pop-up.
  function initFeedVideos(feed) {
    if (!feed || feed.dataset.sfeedVideosInit === '1') return;
    feed.dataset.sfeedVideosInit = '1';

    var videos = feed.querySelectorAll('[data-sfeed-item] .sfeed__video');
    if (!videos.length) return;

    videos.forEach(function (video) {
      video.muted = true;
      video.setAttribute('playsinline', '');
    });

    var autoplay = feed.getAttribute('data-autoplay') === 'true';

    if (autoplay) {
      if (REDUCED_MOTION || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) safePlay(entry.target);
            else entry.target.pause();
          });
        },
        { threshold: 0.4 }
      );
      videos.forEach(function (video) {
        io.observe(video);
      });
      return;
    }

    // Hover-to-play (only where a real pointer/hover exists).
    if (!CAN_HOVER) return;
    feed.querySelectorAll('[data-sfeed-item]').forEach(function (item) {
      var video = item.querySelector('.sfeed__video');
      if (!video) return;
      item.addEventListener('mouseenter', function () {
        safePlay(video);
      });
      item.addEventListener('mouseleave', function () {
        video.pause();
      });
    });
  }

  /* ---------- Multi-image gallery (pop-up) ------------------------------- */
  function initGallery(gallery) {
    if (!gallery || gallery.dataset.sfeedGalleryInit === '1') return;
    gallery.dataset.sfeedGalleryInit = '1';

    var track = gallery.querySelector('[data-sfeed-gallery-track]');
    var dots = Array.prototype.slice.call(
      gallery.querySelectorAll('[data-sfeed-gallery-dot]')
    );
    if (!track || !dots.length) return;

    var prev = gallery.querySelector('[data-sfeed-gallery-prev]');
    var next = gallery.querySelector('[data-sfeed-gallery-next]');
    var current = 0;

    function setActive(index) {
      current = Math.max(0, Math.min(index, dots.length - 1));
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === current);
      });
      if (prev) prev.disabled = current === 0;
      if (next) next.disabled = current === dots.length - 1;
    }

    function goTo(index) {
      var target = Math.max(0, Math.min(index, dots.length - 1));
      track.scrollTo({ left: track.clientWidth * target, behavior: 'smooth' });
      setActive(target);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        goTo(i);
      });
    });

    if (prev)
      prev.addEventListener('click', function () {
        goTo(current - 1);
      });
    if (next)
      next.addEventListener('click', function () {
        goTo(current + 1);
      });

    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = 0;
        setActive(Math.round(track.scrollLeft / track.clientWidth));
      });
    });
  }

  function init(root) {
    var scope = root || document;
    var feeds = scope.querySelectorAll('[data-sfeed]');
    if (feeds.length) {
      feeds.forEach(initFeedVideos);
    } else if (scope.matches && scope.matches('[data-sfeed]')) {
      initFeedVideos(scope);
    }
    scope.querySelectorAll('[data-sfeed-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-scan when a Social Feed section is (re)loaded.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector('[data-sfeed]')) {
      init(event.target);
    }
  });
})();
